import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, LayoutGrid, ChevronLeft, ChevronRight, Cake, Heart, PartyPopper, Printer, CalendarOff, Plus, Maximize2, Minimize2, Rows3 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";
import AddBookingDialog from "./AddBookingDialog";

const GUTTER = 110;

function OccasionIcon({ occ, id, light }) {
  if (!occ) return null;
  const o = occ.toLowerCase();
  const { Icon, cls } = o.includes("birthday")
    ? { Icon: Cake, cls: "text-amber-400" }
    : o.includes("annivers")
      ? { Icon: Heart, cls: "text-rose-400" }
      : { Icon: PartyPopper, cls: "text-primary" };
  return <Icon data-testid={id ? `occasion-icon-${id}` : undefined} title={occ} className={`w-3 h-3 shrink-0 ${light ? "text-white/90" : cls}`} />;
}

const DEFAULT_COLORS = {
  confirmed: "#3B82F6", seated: "#3B82F6", completed: "#22C55E",
  no_show: "#EF4444", pending: "#F59E0B", walkin: "#D97706",
};
function blockColor(b, cc) {
  const occ = (b.occasion || "").toLowerCase();
  if (occ) {
    for (const rule of cc?.occasions || []) {
      if (rule.match && occ.includes(rule.match.toLowerCase())) return rule.color;
    }
  }
  const st = cc?.statuses || {};
  if (b.source === "walkin") return st.walkin || DEFAULT_COLORS.walkin;
  return st[b.status] || DEFAULT_COLORS[b.status] || DEFAULT_COLORS.confirmed;
}
function fmtLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const STATUS_COLORS = {
  confirmed: "border-emerald-400/60 bg-emerald-400/10",
  seated: "border-blue-400/60 bg-blue-400/10",
  completed: "border-zinc-500/60 bg-zinc-500/10",
  no_show: "border-amber-400/60 bg-amber-400/10",
  pending: "border-orange-400/60 bg-orange-400/10",
};

function toMin(t) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function toHHMM(min) { return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`; }
function dayKey(dateStr) { return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }).toLowerCase(); }

export default function CalendarTab({ restaurant, canEdit = false, onSaved }) {
  const [date, setDate] = useState(fmtLocal(new Date()));
  const [view, setView] = useState("day");
  const [tables, setTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [week, setWeek] = useState({});
  const [avail, setAvail] = useState(null);
  const [selected, setSelected] = useState(null);
  const [moveDate, setMoveDate] = useState("");
  const [moveTime, setMoveTime] = useState("");
  const [moveSlots, setMoveSlots] = useState([]);
  const gridRef = useRef(null);
  const [colW, setColW] = useState(88);
  const [dragEval, setDragEval] = useState(null);
  const evalTimeRef = useRef("");
  const optSeq = useRef(0);
  const loadSeq = useRef(0);
  const [epoch, setEpoch] = useState(0);
  const [closedDates, setClosedDates] = useState(restaurant.booking_policy?.closed_dates || []);
  const [closeDialog, setCloseDialog] = useState(false);
  const [closing, setClosing] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null);
  const [selTables, setSelTables] = useState([]);
  const [density, setDensity] = useState(() => localStorage.getItem(`cal-density-${restaurant.id}`) || "cozy");
  const rowH = density === "compact" ? 30 : 44;
  const toggleDensity = () => {
    const next = density === "compact" ? "cozy" : "compact";
    setDensity(next);
    localStorage.setItem(`cal-density-${restaurant.id}`, next);
  };
  const [isFs, setIsFs] = useState(false);
  const fsRef = useRef(null);
  const calColors = restaurant.calendar_colors;
  useEffect(() => {
    if (!isFs) return;
    const h = (e) => { if (e.key === "Escape" && !e.defaultPrevented) setIsFs(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [isFs]);
  const toggleFull = () => setIsFs((v) => !v);
  useEffect(() => { setClosedDates(restaurant.booking_policy?.closed_dates || []); }, [restaurant.booking_policy]);
  const isClosedDate = closedDates.includes(date);

  const toggleCloseDay = async () => {
    setClosing(true);
    try {
      const bp = { ...(restaurant.booking_policy || {}) };
      bp.closed_dates = isClosedDate ? closedDates.filter((d) => d !== date) : [...closedDates, date];
      await api.put(`/restaurants/${restaurant.id}`, { booking_policy: bp });
      setClosedDates(bp.closed_dates);
      toast.success(isClosedDate ? "Day reopened for online bookings" : "Day closed to online bookings");
      setCloseDialog(false);
      onSaved?.();
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setClosing(false);
  };

  const fetchOptions = (b, time) => {
    const seq = ++optSeq.current;
    api.get(`/restaurants/${restaurant.id}/table-options`, {
      params: { date, time, party_size: b.party_size, booking_id: b.id },
    }).then((r) => { if (seq === optSeq.current) setDragEval({ time, ...r.data }); })
      .catch(() => { if (seq === optSeq.current) setDragEval(null); });
  };

  const weekDates = useMemo(() => {
    if (!date) return [];
    const d = new Date(date + "T00:00:00");
    if (isNaN(d.getTime())) return [];
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const x = new Date(monday);
      x.setDate(monday.getDate() + i);
      return fmtLocal(x);
    });
  }, [date]);

  const load = () => {
    const seq = ++loadSeq.current;
    api.get(`/restaurants/${restaurant.id}/tables`).then((r) => { if (seq === loadSeq.current) setTables(r.data.filter((t) => t.active)); }).catch(() => {});
    if (view === "day") {
      api.get(`/restaurants/${restaurant.id}/bookings`, { params: { date } })
        .then((r) => { if (seq === loadSeq.current) setBookings(r.data); }).catch(() => {});
      api.get(`/restaurants/${restaurant.id}/availability`, { params: { date, party_size: 2 } })
        .then((r) => { if (seq === loadSeq.current) setAvail(r.data); }).catch(() => { if (seq === loadSeq.current) setAvail(null); });
    } else {
      Promise.all(
        weekDates.map((d) =>
          api.get(`/restaurants/${restaurant.id}/bookings`, { params: { date: d } }).then((r) => [d, r.data]).catch(() => [d, []])
        )
      ).then((entries) => { if (seq === loadSeq.current) setWeek(Object.fromEntries(entries)); });
    }
  };
  useEffect(() => { load(); }, [date, view, restaurant.id]);

  useEffect(() => {
    if (!selected || !moveDate) { setMoveSlots([]); return; }
    let live = true;
    api.get(`/restaurants/${restaurant.id}/availability`, { params: { date: moveDate, party_size: selected.party_size } })
      .then((r) => {
        if (!live) return;
        const times = (r.data.slots || []).map((s) => s.time);
        if (moveDate === selected.date && selected.time && !times.includes(selected.time)) times.push(selected.time);
        times.sort();
        setMoveSlots(times);
        setMoveTime((prev) => (times.includes(prev) ? prev : times[0] || ""));
      })
      .catch(() => { if (live) setMoveSlots([]); });
    return () => { live = false; };
  }, [moveDate, selected?.id]);

  const patchBooking = async (bid, body, msg) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/bookings/${bid}`, body);
      if (msg) toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(fmtErr(e));
      return false;
    }
  };

  const shift = (n) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + (view === "week" ? n * 7 : n));
    setDate(fmtLocal(d));
  };

  const day = restaurant.operating_hours?.[dayKey(date)];
  const isToday = date === fmtLocal(new Date());
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const nextSlot = useMemo(() => {
    const slots = (avail?.slots || []).map((s) => s.time);
    if (!slots.length) return null;
    if (!isToday) return slots[0];
    return slots.find((t) => toMin(t) > nowMin + 15) || null;
  }, [avail, isToday, nowMin]);
  const colCount = useMemo(() => {
    if (!day || day.closed) return 0;
    return Math.max(1, Math.floor((toMin(day.close) - toMin(day.open)) / 30));
  }, [day]);

  useEffect(() => {
    const measure = () => {
      if (gridRef.current && colCount > 0) {
        setColW(Math.max(42, (gridRef.current.clientWidth - GUTTER) / colCount));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 100);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [colCount, view, date, isFs]);  const duration = restaurant.booking_policy?.slot_duration ?? 90;
  const active = bookings.filter((b) => b.status !== "cancelled" && b.table_id);

  const selOccIds = new Set();
  if (selected) {
    const s0 = toMin(selected.time);
    const dur0 = restaurant.booking_policy?.slot_duration ?? 90;
    bookings.filter((b) => b.id !== selected.id && ["confirmed", "seated", "pending"].includes(b.status)).forEach((b) => {
      const bs = toMin(b.time), be = bs + (b.duration || dur0) + 15;
      if (bs < s0 + dur0 + 15 && s0 < be) {
        (b.table_ids || [b.table_id]).filter(Boolean).forEach((tid) => {
          selOccIds.add(tid);
          const tt = tables.find((x) => x.id === tid);
          if (tt?.parent_id) selOccIds.add(tt.parent_id);
          tables.forEach((x) => { if (x.parent_id === tid) selOccIds.add(x.id); });
        });
      }
    });
  }

  const renderDay = () => {
    if (!day || day.closed) {
      return <p data-testid="calendar-closed-msg" className="text-muted-foreground py-16 text-center">Closed on this day.</p>;
    }
    const openM = toMin(day.open);
    const cols = Math.max(1, Math.floor((toMin(day.close) - openM) / 30));
    const spanCols = Math.max(1, Math.round(duration / 30));
    return (
      <div ref={gridRef} className={`overflow-auto dash-scroll ${isFs ? "max-h-[calc(100vh-170px)]" : "max-h-[calc(100vh-235px)]"} rounded-2xl border border-border/60 bg-card`} data-testid="calendar-day-grid">
        <div style={{ minWidth: GUTTER + cols * colW, width: "100%" }}>
          <div className="flex border-b border-border/60 bg-card sticky top-0 z-30">
            <div style={{ width: GUTTER }} className="shrink-0 px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-end sticky left-0 z-40 bg-card">
              Tables
            </div>
            {Array.from({ length: cols }, (_, i) => (
              <div key={i} style={{ width: colW }} className="shrink-0 text-[10px] text-muted-foreground px-1.5 py-2 border-l border-border/40">
                {i % 2 === 0 ? toHHMM(openM + i * 30) : ""}
              </div>
            ))}
          </div>
          <div className="relative" style={{ height: Math.max(1, tables.length) * rowH, width: GUTTER + cols * colW }}>
            {tables.map((t, i) => (
              <div key={t.id} data-testid={`cal-row-${t.id}`}
                onClick={(e) => {
                  if (!canEdit) return;
                  const x = e.clientX - e.currentTarget.getBoundingClientRect().left - GUTTER;
                  if (x < 0) return;
                  const ci = Math.min(cols - 1, Math.max(0, Math.floor(x / colW)));
                  setQuickAdd({ date, time: toHHMM(openM + ci * 30), table_id: t.id });
                }}
                className={`absolute left-0 right-0 border-t border-border/40 flex items-center ${i % 2 ? "bg-muted/20" : ""} ${canEdit ? "cursor-cell" : ""}`}
                style={{ top: i * rowH, height: rowH }}>
                <div style={{ width: GUTTER }} className={`pl-3 shrink-0 sticky left-0 z-10 ${i % 2 ? "bg-muted" : "bg-card"}`}>
                  <p className={`${density === "compact" ? "text-xs" : "text-sm"} font-outfit font-medium`}>{t.parent_id ? "└ " : ""}{density === "compact" ? `(${t.capacity}) ` : ""}{t.label}</p>
                  {density !== "compact" && <p className="text-[10px] text-muted-foreground">{t.capacity} seats</p>}
                </div>
                {dragEval && (() => {
                  const o = (dragEval.options || []).find((x) => x.id === t.id);
                  if (!o) return null;
                  const cls = o.state === "best" ? "bg-emerald-400/15" : o.state === "ok" ? "bg-primary/10" : "bg-red-400/10";
                  return <div data-testid={`drag-hint-${t.id}`} style={{ left: GUTTER }}
                    className={`absolute right-0 top-0 bottom-0 pointer-events-none transition-colors duration-150 ${cls}`} />;
                })()}
                {(() => {
                  const cb = active.find((b) => (b.table_ids || []).length > 1 && b.table_ids[0] !== t.id && b.table_ids.includes(t.id));
                  if (!cb) return null;
                  const cbCol = Math.max(0, Math.round((toMin(cb.time) - openM) / 30));
                  const cbSpan = spanCols;
                  return (
                    <div data-testid={`combo-block-${cb.id}-${t.id}`}
                      className="absolute rounded-md border border-dashed border-primary/40 bg-primary/5 pointer-events-none flex items-center overflow-hidden"
                      style={{ left: GUTTER + cbCol * colW + 3, width: cbSpan * colW - 6, top: 5, bottom: 5 }}>
                      <p className="text-[10px] text-muted-foreground truncate px-1.5">Linked: {cb.table_label}</p>
                    </div>
                  );
                })()}
              </div>
            ))}
            <div className="absolute left-0 right-0 border-t border-border/40" style={{ top: tables.length * rowH }} />
            {Array.from({ length: cols + 1 }, (_, i) => (
              <div key={i} className="absolute border-l border-border/20" style={{ left: GUTTER + i * colW, top: 0, bottom: 0 }} />
            ))}
            {isToday && day && !day.closed && nowMin >= toMin(day.open) && nowMin <= toMin(day.close) && (
              <div data-testid="now-line" className="absolute z-20 pointer-events-none" style={{ left: GUTTER + ((nowMin - toMin(day.open)) / 30) * colW, top: 0, bottom: 0 }}>
                <div className="absolute -top-1 -left-[5px] w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-px h-full bg-red-400/80" />
              </div>
            )}
            {tables.length === 0 && (
              <p className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm" data-testid="calendar-no-tables">
                No tables yet — add tables in the Floor plan tab.
              </p>
            )}
            {active.map((b) => {
              const rowIdx = tables.findIndex((t) => t.id === b.table_id);
              if (rowIdx === -1) return null;
              const colIdx = Math.max(0, Math.round((toMin(b.time) - openM) / 30));
              const w = spanCols * colW - 6;
              return (
                <motion.div
                  key={`${b.id}-${b.time}-${b.table_id}-${b.status}-${epoch}`}
                  data-testid={`cal-booking-${b.id}`}
                  drag
                  dragMomentum={false}
                  onTap={() => { setSelected(b); setMoveDate(b.date); setMoveTime(b.time); setSelTables((b.table_ids || [b.table_id]).filter(Boolean)); }}
                  onDragStart={() => { evalTimeRef.current = b.time; fetchOptions(b, b.time); }}
                  onDrag={(e, info) => {
                    const dCol = Math.round(info.offset.x / colW);
                    const newCol = Math.min(Math.max(0, cols - spanCols), Math.max(0, colIdx + dCol));
                    const t2 = toHHMM(openM + newCol * 30);
                    if (t2 !== evalTimeRef.current) {
                      evalTimeRef.current = t2;
                      fetchOptions(b, t2);
                    }
                  }}
                  onDragEnd={(e, info) => {
                    setDragEval(null);
                    setEpoch((n) => n + 1);
                    const dCol = Math.round(info.offset.x / colW);
                    const dRow = Math.round(info.offset.y / rowH);
                    const target = tables[rowIdx + dRow];
                    const newCol = Math.min(Math.max(0, cols - spanCols), Math.max(0, colIdx + dCol));
                    const body = {};
                    const newTime = toHHMM(openM + newCol * 30);
                    if (newTime !== b.time) body.time = newTime;
                    if (target && target.id !== b.table_id) body.table_id = target.id;
                    if (Object.keys(body).length) patchBooking(b.id, body, "Booking moved");
                    else load();
                  }}
                  style={{
                    left: GUTTER + colIdx * colW + 3,
                    top: rowIdx * rowH + 4,
                    width: w,
                    height: rowH - 8,
                    backgroundColor: blockColor(b, calColors),
                  }}
                  className={`group absolute z-10 cursor-grab active:cursor-grabbing rounded-xl text-white shadow-md hover:shadow-lg transition-shadow ${density === "compact" ? "px-2 py-0" : "px-2.5 py-0.5"} ${
                    b.status === "seated" ? "ring-2 ring-white/70" : ""
                  } ${b.status === "pending" || b.status === "no_show" ? "opacity-70 border border-dashed border-white/60" : ""}`}
                >
                  <p className={`${density === "compact" ? "text-[10px]" : "text-xs"} font-semibold truncate leading-tight flex items-center gap-1`}>
                    <OccasionIcon occ={b.occasion} id={b.id} light />
                    <span className="truncate">{b.name}{density === "compact" ? ` · ${b.time} · ${b.party_size}` : ""}</span>
                  </p>
                  {density !== "compact" && (
                    <p className="text-[10px] text-white/85 truncate leading-tight">
                      {b.time} · {b.party_size} guests{(b.table_ids || []).length > 1 ? " · combined" : ""}{b.status !== "confirmed" ? ` · ${b.status.replace("_", " ")}` : ""}
                    </p>
                  )}
                  <div data-testid={`cal-hover-${b.id}`}
                    className={`pointer-events-none absolute left-1/2 -translate-x-1/2 ${rowIdx < 2 ? "top-full mt-1.5" : "bottom-full mb-1.5"} z-50 w-60 rounded-xl border border-border/60 bg-popover p-3 text-xs text-popover-foreground shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150`}>
                    <p className="font-medium text-sm leading-tight text-left">{b.name} · {b.party_size} guests</p>
                    <p className="text-muted-foreground mt-0.5 text-left">
                      {b.time}–{toHHMM(toMin(b.time) + (b.duration || restaurant.booking_policy?.slot_duration || 90))} · {tables[rowIdx]?.label}
                    </p>
                    {b.phone && <p className="mt-1 text-left">{b.phone}</p>}
                    {b.occasion && <p className="mt-1 text-primary text-left">Occasion: {b.occasion}</p>}
                    {b.dietary && <p className="mt-1 text-amber-500 text-left">Dietary: {b.dietary}</p>}
                    {b.notes && <p className="mt-1 text-left">Notes: {b.notes}</p>}
                    <p className="mt-1 capitalize text-muted-foreground text-left">{b.status.replace("_", " ")}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWeek = () => (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="calendar-week-grid">
      {weekDates.map((d) => {
        const items = (week[d] || []).filter((b) => b.status !== "cancelled");
        const dObj = new Date(d + "T00:00:00");
        const isToday = d === fmtLocal(new Date());
        return (
          <button key={d} data-testid={`week-day-${d}`} onClick={() => { setDate(d); setView("day"); }}
            className={`text-left rounded-xl border p-3 min-h-[160px] transition-colors duration-200 ${
              isToday ? "border-primary/60 bg-primary/5" : "border-border/60 bg-card hover:border-border"
            }`}>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{dObj.toLocaleDateString("en-US", { weekday: "short" })}</p>
            <p className="font-outfit font-medium text-lg">{dObj.getDate()}</p>
            <p className="text-[10px] text-muted-foreground mb-2">{items.length} booking{items.length === 1 ? "" : "s"}</p>
            {closedDates.includes(d) && (
              <p data-testid={`week-closed-${d}`} className="text-[10px] text-red-500 font-medium mb-1">Closed</p>
            )}
            <div className="space-y-1">
              {items.slice(0, 5).map((b) => (
                <div key={b.id} style={{ backgroundColor: blockColor(b, calColors) }}
                  className={`text-[10px] text-white rounded-md px-1.5 py-1 truncate flex items-center gap-1 ${b.status === "pending" || b.status === "no_show" ? "opacity-70" : ""}`}>
                  <OccasionIcon occ={b.occasion} light />
                  <span className="truncate">{b.time} {b.name} · {b.party_size}</span>
                </div>
              ))}
              {items.length > 5 && <p className="text-[10px] text-muted-foreground">+{items.length - 5} more</p>}
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={fsRef} data-testid="calendar-tab" className={isFs ? "fixed inset-0 z-50 bg-background overflow-y-auto p-4 sm:p-8" : ""}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev-btn" onClick={() => shift(-1)} aria-label="Previous"
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Input data-testid="calendar-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-card border-border/60 max-w-[200px]" />
          <button data-testid="cal-next-btn" onClick={() => shift(1)} aria-label="Next"
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-border transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button data-testid="cal-today-btn" onClick={() => setDate(fmtLocal(new Date()))}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors ml-1">
            Today
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <Button data-testid="cal-add-booking-btn" size="sm" className="rounded-full"
              onClick={() => setQuickAdd({ date, time: nextSlot || "19:00", table_id: "" })}>
              <Plus className="w-4 h-4 mr-2" /> Add booking
            </Button>
          )}
          <Button data-testid="cal-fullscreen-btn" size="sm" variant="outline" className="rounded-full" onClick={toggleFull}>
            {isFs ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />} {isFs ? "Exit" : "Full screen"}
          </Button>
          <Button data-testid="cal-density-btn" size="sm" variant="outline" className="rounded-full" onClick={toggleDensity}>
            <Rows3 className="w-4 h-4 mr-2" /> {density === "compact" ? "Comfortable" : "Compact"}
          </Button>
          <Button data-testid="print-guest-list-btn" size="sm" variant="outline" className="rounded-full"
            onClick={() => window.open(`/print/guest-list/${restaurant.id}/${date}`, "_blank")}>
            <Printer className="w-4 h-4 mr-2" /> Guest list
          </Button>
          {canEdit && (
            <Button data-testid="close-day-btn" size="sm" variant={isClosedDate ? "default" : "outline"} className="rounded-full"
              onClick={() => setCloseDialog(true)}>
              <CalendarOff className="w-4 h-4 mr-2" /> {isClosedDate ? "Reopen day" : "Close day"}
            </Button>
          )}
          <Button data-testid="view-day-btn" size="sm" variant={view === "day" ? "default" : "outline"} className="rounded-full" onClick={() => setView("day")}>
            <CalendarDays className="w-4 h-4 mr-2" /> Day
          </Button>
          <Button data-testid="view-week-btn" size="sm" variant={view === "week" ? "default" : "outline"} className="rounded-full" onClick={() => setView("week")}>
            <LayoutGrid className="w-4 h-4 mr-2" /> Week
          </Button>
        </div>
      </div>
      {view === "day" && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="day-glance">
          {isClosedDate && (
            <span data-testid="closed-day-chip" className="text-xs rounded-full bg-red-400/15 text-red-500 border border-red-400/40 px-3 py-1.5 font-medium">
              Closed to online bookings
            </span>
          )}
          <span data-testid="glance-counts" className="text-xs rounded-full border border-border/60 bg-card px-3 py-1.5">
            {active.length} booking{active.length === 1 ? "" : "s"} · {active.reduce((n, b) => n + (b.party_size || 0), 0)} covers
          </span>
          <span data-testid="glance-next-slot" className={`text-xs rounded-full px-3 py-1.5 font-medium ${nextSlot ? "bg-primary/15 text-primary border border-primary/40" : "border border-border/60 text-muted-foreground"}`}>
            {nextSlot ? `Next free table ${nextSlot}` : "No free slots this day"}
          </span>
          {avail?.slots?.length > 0 && (
            <span data-testid="glance-open-slots" className="text-xs rounded-full border border-border/60 text-muted-foreground px-3 py-1.5">
              {avail.slots.length} open slot{avail.slots.length === 1 ? "" : "s"} (party of 2)
            </span>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground mb-4">Drag a booking sideways to change its time, up or down to change table. While dragging, rows highlight: green = best fit for the party, red = blocked. Tap for details or to move its date.</p>
      {view === "day" ? (
        <>
          {renderDay()}
          {dragEval && (
            <p data-testid="drag-suggestion-hint" className="text-xs text-muted-foreground mt-3">
              {!dragEval.valid_slot
                ? `${dragEval.time} is outside bookable slots — all rows blocked.`
                : dragEval.best
                  ? <>Best fit at {dragEval.time}: <span className="text-emerald-500 font-medium">{dragEval.best.label} · {dragEval.best.capacity} seats</span>. Green rows fit this party; red rows are blocked.</>
                  : `No table free at ${dragEval.time} for this party.`}
            </p>
          )}
        </>
      ) : renderWeek()}

      <AddBookingDialog restaurant={restaurant} tables={tables} open={!!quickAdd}
        onOpenChange={() => setQuickAdd(null)} preset={quickAdd} onAdded={load} />

      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="bg-card border-border/60">
          <DialogHeader>
            <DialogTitle className="font-outfit">{isClosedDate ? "Reopen" : "Close off"} {date}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2" data-testid="close-day-dialog-body">
            {isClosedDate ? (
              <p>Online bookings will be accepted again for this date.</p>
            ) : (
              <>
                <p>New online bookings will be blocked for this date. You and your team can still add bookings manually.</p>
                {active.length > 0 && (
                  <p data-testid="close-day-warning" className="text-amber-500">
                    Warning: this day already has {active.length} booking{active.length === 1 ? "" : "s"} ({active.reduce((n, b) => n + (b.party_size || 0), 0)} covers) — they will not be cancelled:
                  </p>
                )}
                {active.length > 0 && (
                  <ul className="max-h-40 overflow-y-auto dash-scroll space-y-1 pr-1">
                    {active.map((b) => (
                      <li key={b.id} className="text-xs border border-border/40 rounded-lg px-2.5 py-1.5">
                        {b.time} · {b.name} · party of {b.party_size}{b.table_label ? ` · ${b.table_label}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button data-testid="close-day-cancel-btn" variant="outline" className="rounded-full" onClick={() => setCloseDialog(false)}>Cancel</Button>
            <Button data-testid="close-day-confirm-btn" className="rounded-full" disabled={closing} onClick={toggleCloseDay}>
              {closing ? "Saving..." : isClosedDate ? "Reopen day" : "Close day"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-card border-border/60">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">{selected.name} · {selected.time}</DialogTitle></DialogHeader>
              <div className="text-sm text-muted-foreground space-y-1.5" data-testid="cal-booking-detail">
                <p>{selected.date} · Party of {selected.party_size} · Table {selected.table_label}</p>
                <p>{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</p>
                {selected.occasion && <p data-testid="cal-occasion" className="text-primary">Occasion: {selected.occasion}</p>}
                {selected.notes && <p data-testid="cal-notes">Notes: {selected.notes}</p>}
                {selected.dietary && <p className="text-amber-500">Dietary: {selected.dietary}</p>}
              </div>
              <div className="mt-4 space-y-3">
                {canEdit && (
                  <div data-testid="cal-tables-picker">
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">
                      Tables · {tables.filter((t) => selTables.includes(t.id)).reduce((n, t) => n + (t.capacity || 0), 0)} seats selected
                    </Label>
                    <div className="mt-1.5 flex flex-wrap gap-2 max-h-28 overflow-y-auto dash-scroll">
                      {tables.map((t) => {
                        const cur = (selected.table_ids || [selected.table_id] || []).filter(Boolean).includes(t.id);
                        const busy = selOccIds.has(t.id) && !cur;
                        const on = selTables.includes(t.id);
                        return (
                          <button key={t.id} type="button" data-testid={`cal-tables-${t.id}`} disabled={busy}
                            onClick={() => setSelTables(on ? selTables.filter((x) => x !== t.id) : [...selTables, t.id])}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${busy ? "opacity-40 cursor-not-allowed border-border/40 text-muted-foreground line-through" : on ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                            {t.parent_id ? "└ " : ""}{t.label} ({t.capacity})
                          </button>
                        );
                      })}
                    </div>
                    <Button data-testid="cal-save-tables-btn" size="sm" variant="outline" className="rounded-full mt-2"
                      disabled={!selTables.length || [...selTables].sort().join() === [...(selected.table_ids || [selected.table_id])].filter(Boolean).sort().join()}
                      onClick={async () => { if (await patchBooking(selected.id, { table_ids: selTables }, "Tables updated")) setSelected(null); }}>
                      Save tables
                    </Button>
                  </div>
                )}
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Move booking</Label>
                  <div className="flex items-end gap-2 mt-1.5">
                    <Input data-testid="cal-move-date" type="date" value={moveDate} min={fmtLocal(new Date())}
                      onChange={(e) => setMoveDate(e.target.value)} className="bg-background border-border/60 flex-1" />
                    <Select value={moveTime} onValueChange={setMoveTime}>
                      <SelectTrigger data-testid="cal-move-time" className="w-[110px] bg-background border-border/60">
                        <SelectValue placeholder="Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {moveSlots.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`cal-move-time-${t.replace(":", "")}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button data-testid="cal-move-date-btn" size="sm" variant="outline" className="rounded-full"
                      disabled={!moveDate || (moveDate === selected.date && (!moveTime || moveTime === selected.time)) || (moveSlots.length > 0 && !moveTime)}
                      onClick={async () => {
                        const body = {};
                        if (moveDate !== selected.date) body.date = moveDate;
                        if (moveTime && moveTime !== selected.time) body.time = moveTime;
                        if (!Object.keys(body).length) return;
                        if (await patchBooking(selected.id, body, "Booking moved")) setSelected(null);
                      }}>
                      Move
                    </Button>
                  </div>
                  {moveSlots.length === 0 && (
                    <p className="text-[11px] text-amber-500 mt-1.5">No bookable times on this date.</p>
                  )}
                </div>
                <Select value="" onValueChange={(v) => { patchBooking(selected.id, { status: v }, "Status updated"); setSelected(null); }}>
                  <SelectTrigger data-testid="cal-status-select" className="bg-background border-border/60">
                    <SelectValue placeholder="Update status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="seated">Seated</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="no_show">No show</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="cal-detail-done-btn" className="w-full rounded-full" onClick={() => setSelected(null)}>
                  Confirm
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
